import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { SectionIntro } from '../alt/SectionIntro';
import { AgentBuilderCard, ApprovalCard, CategoryChip, ConnectionsCard, SharedAgentsCard, TemplateWall } from './agentArtefacts';
const APPROVE_IMAGE = 'https://cdn.magicpatterns.com/patterns/generated-images/91f40c7c-271e-4f94-b6a5-96decd1c93af.jpg';
const CONNECT_IMAGE = 'https://cdn.magicpatterns.com/uploads/hJZmgqcQ1jKjsQ1YwRUstr/hf_20260906_211028_5de5584d-d238-4fb1-afb6-dba925712826.png';
const EASE = [0.16, 1, 0.3, 1] as const;
type PanelProps = {
  children: React.ReactNode;
  className?: string;
  tint?: 'white' | 'peach' | 'sand';
  delay?: number;
};
function Panel({
  children,
  className = '',
  tint = 'white',
  delay = 0
}: PanelProps) {
  const reduce = useReducedMotion();
  const surface = tint === 'peach' ? 'bg-[#FFF4EC]' : tint === 'sand' ? 'bg-[#F7F2EA]' : 'bg-white';
  return <motion.article initial={reduce ? false : {
    opacity: 0,
    y: 18
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-90px'
  }} transition={{
    duration: 0.3,
    delay,
    ease: EASE
  }} className={`relative flex min-w-0 flex-col overflow-hidden rounded-[22px] border border-[#E7E0D4] ${surface} shadow-[0_1px_2px_rgba(43,33,26,0.04),0_24px_60px_-40px_rgba(43,33,26,0.35)] ${className}`} data-id="element-269" data-mp="1kp5g2wa9jhuu">
      {children}
    </motion.article>;
}
type PanelCopyProps = {
  title: string;
  copy: string;
  className?: string;
  children?: React.ReactNode;
};
function PanelCopy({
  title,
  copy,
  className = '',
  children
}: PanelCopyProps) {
  return <div className={className} data-id="element-270" data-mp="jtxdhokm45qb">
      <h3 className="font-title text-[24px] font-bold leading-[1.12] tracking-[-0.025em] text-[#2B211A] md:text-[27px]" data-id="element-271" data-mp="jqrpydrmtbwp">
        {title}
        <span className="text-[#EA580C]" data-id="element-272" data-mp="arei1pj5ove3">.</span>
      </h3>
      <p className="mt-3 max-w-[42ch] text-[14.5px] leading-[1.65] text-[#6B5D52] md:text-[15px]" data-id="element-273" data-mp="au0v24otg33p">
        {copy}
      </p>
      {children}
    </div>;
}
function ApprovalVisual() {
  return <div className="relative h-[360px] min-h-0 overflow-hidden rounded-[18px] bg-[#E8DED0] md:min-h-[420px] xl:h-full xl:rounded-l-none xl:rounded-r-[21px]" data-id="element-274" data-mp="2hi9nvukw891w">
      <img src={APPROVE_IMAGE} alt="" aria-hidden="true" loading="lazy" className="absolute inset-0 size-full object-cover object-center" data-id="element-275" data-mp="27rezajk6cxda" />
      <div className="absolute inset-0 bg-[#2B211A]/[0.08]" aria-hidden="true" data-id="element-276" data-mp="30favzo9qupa2" />
      <div className="absolute inset-x-3 top-1/2 -translate-y-1/2 sm:inset-x-5 md:inset-x-6" data-id="element-277" data-mp="2vg7q61lxwf11">
        <ApprovalCard variant="glass" data-id="element-278" data-mp="2qgl9tjateio0" />
      </div>
    </div>;
}
function ConnectionsVisual() {
  return <div className="relative h-[340px] overflow-hidden rounded-[18px] bg-[#EAE3D8] md:min-h-[410px] lg:h-full lg:rounded-l-none lg:rounded-r-[21px]" data-id="element-279" data-mp="2mft06oy16br7">
      <img src={CONNECT_IMAGE} alt="" aria-hidden="true" loading="lazy" className="absolute inset-0 size-full object-cover object-[18%_70%]" data-id="element-280" data-mp="1f31389cncb4d" />
      <div aria-hidden="true" className="absolute inset-0 bg-[linear-gradient(to_right,rgba(250,248,244,0.05)_0%,rgba(250,248,244,0.18)_55%,rgba(250,248,244,0.48)_100%)]" data-id="element-281" data-mp="3mp16ezqz3bvh" />
      <div className="absolute bottom-6 left-6 right-6 sm:right-auto sm:w-[380px] md:bottom-8 md:left-8" data-id="element-282" data-mp="3vml7o8ou5863">
        <ConnectionsCard data-id="element-283" data-mp="4b1pk7qhh2bo" />
      </div>
    </div>;
}
export function AgentFeatureMosaic() {
  return <div data-id="element-284" data-mp="98a849fjn8dg">
      <SectionIntro align="left" className="max-w-[720px]" data-id="element-285" data-mp="9anzo8b0m2g9">
        <h2 className="font-title text-[34px] font-bold leading-[1.08] tracking-[-0.03em] text-[#2B211A] md:text-[52px]" data-id="element-286" data-mp="e6lda6uw8upc">
          Built for the work that repeats<span className="text-[#EA580C]" data-id="element-287" data-mp="n8cjrdx9d5k9">.</span>
        </h2>
        <p className="mt-5 max-w-[54ch] text-[16.5px] leading-relaxed text-[#6B5D52] md:text-lg" data-id="element-288" data-mp="n7t5c76tgzaz">
          Move past one-off prompts. Hand a recurring responsibility to an agent that knows your
          company, reads the systems you already run on, and reports back where your team already
          works.
        </p>
      </SectionIntro>

      <div className="mt-12 grid grid-cols-1 gap-4 md:mt-16 lg:grid-cols-12 lg:gap-5" data-id="element-289" data-mp="1oqomksnszms7">
        <Panel tint="peach" className="lg:col-span-7 lg:min-h-[470px]" data-id="element-290" data-mp="23np463ca61cy">
          <div className="grid h-full min-w-0 grid-cols-1 xl:grid-cols-[minmax(0,0.68fr)_minmax(330px,1.32fr)]" data-id="element-291" data-mp="23mxj3mgb7m0x">
            <PanelCopy title="Create an agent in minutes" copy="Describe the role, the priorities and the outcomes it should care about — no code, no flowchart. Then choose where it runs, what it is allowed to reach, and which model sits behind it." className="p-6 sm:p-7 md:pr-5" data-id="element-292" data-mp="1ymrnxwatzhe3">
              <div className="mt-6 flex flex-wrap gap-2" data-id="element-293" data-mp="1fxl4u864knrr">
                <CategoryChip category="marketing" data-id="element-294" data-mp="1fw1nsfcna8ox" />
                <CategoryChip category="sales" data-id="element-295" data-mp="s7icdqyatfsm" />
                <CategoryChip category="people" data-id="element-296" data-mp="s6787q0xy60z" />
              </div>
            </PanelCopy>
            <div className="flex min-w-0 items-end px-4 pb-6 sm:px-6 md:pb-7" data-id="element-297" data-mp="n7crctmjarji">
              <div className="mx-auto w-full max-w-[520px]" data-id="element-298" data-mp="2yv7xsu2cqfr1">
                <AgentBuilderCard data-id="element-299" data-mp="33nsm5g2ho0n6" />
              </div>
            </div>
          </div>
        </Panel>

        <Panel className="lg:col-span-5 lg:min-h-[470px]" delay={0.07} data-id="element-300" data-mp="2ozeb4hux55vj">
          <PanelCopy title="Start fast with pre-built templates" copy="Kick off with agents designed for real jobs across sales, marketing, support, finance and HR. Then change anything about them — the brief, the schedule, the tools — to match the way your team actually works." className="p-6 pb-0 sm:p-7 sm:pb-0" data-id="element-301" data-mp="2txpmix0v3ofv" />
          <div className="mt-auto overflow-hidden pt-3" data-id="element-302" data-mp="38mca1m0n7mi6">
            <TemplateWall data-id="element-303" data-mp="3cnfpw8ioxije" />
          </div>
        </Panel>

        <Panel tint="sand" className="lg:col-span-12 lg:min-h-[430px]" data-id="element-304" data-mp="3cq1v41i3zlyg">
          <div className="grid h-full min-w-0 grid-cols-1 lg:grid-cols-[minmax(300px,0.68fr)_minmax(0,1.32fr)]" data-id="element-305" data-mp="1x5vrvkxperrm">
            <PanelCopy title="Securely connect your work tools" copy="Generic AI doesn't know your business; a Giga agent does. Connections are made once, centrally, and every agent reaches your systems through them — never through somebody's personal login." className="p-6 sm:p-7 md:self-center md:p-9 lg:p-11" data-id="element-306" data-mp="216xqq0npl1t2" />
            <ConnectionsVisual data-id="element-307" data-mp="3p61k6gyice8e" />
          </div>
        </Panel>

        <Panel className="lg:col-span-7 lg:min-h-[460px]" data-id="element-308" data-mp="3l4y7g3braybe">
          <div className="grid h-full min-w-0 grid-cols-1 xl:grid-cols-[minmax(235px,0.66fr)_minmax(0,1.34fr)]" data-id="element-309" data-mp="3l6j17qq8yd87">
            <PanelCopy title="Stay in control while agents work" copy="Before an agent sends a message, files a ticket or updates a record, it stops and shows you exactly what is about to happen. Approve it once, allow it always, or decline — every time, on every action." className="p-6 sm:p-7 md:self-center md:pr-5" data-id="element-310" data-mp="3g8hij6wqgfx9" />
            <ApprovalVisual data-id="element-311" data-mp="14h3uavwwbrx0" />
          </div>
        </Panel>

        <Panel tint="peach" className="lg:col-span-5 lg:min-h-[460px]" delay={0.07} data-id="element-312" data-mp="1cye5zis9b2nb">
          <PanelCopy title="Share an agent with the whole team" copy="An agent built once belongs to the group, not to the person who made it. Access follows the group it sits in, so the right people get it on day one and nobody quietly rebuilds the same thing." className="p-6 pb-0 sm:p-7 sm:pb-0" data-id="element-313" data-mp="1gyy3ingqs89z" />
          <div className="mt-auto px-4 pt-5 sm:px-6" data-id="element-314" data-mp="u0bcorsoejjp">
            <SharedAgentsCard data-id="element-315" data-mp="xzsgzhwd9ana" />
          </div>
        </Panel>
      </div>
    </div>;
}